// Service layer for Web 2.0 / Article Submission feature.
import apiService from './api';

/** Suggested Web 2.0 platforms shown after article generation (mirrors PDF module pattern). */
export const WEB2_SUGGESTED_PLATFORMS = [
  { name: 'Sites Google',  icon: 'ri-google-line',       color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800',    da: 97 },
  { name: 'Medium',        icon: 'ri-medium-line',       color: 'bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',     da: 95 },
  { name: 'WordPress.com', icon: 'ri-wordpress-line',    color: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800', da: 93 },
  { name: 'Blogger',       icon: 'ri-blogger-line',      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-800', da: 91 },
  { name: 'Weebly',        icon: 'ri-layout-masonry-line', color: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border-teal-100 dark:border-teal-800',   da: 89 },
];

export interface Web20Platform { name: string; da: number; url: string; }
export interface Web20Article {
  id: string; platform: string; platform_da: number; platform_url: string | null;
  title: string; content: string; word_count: number; backlink_url: string | null;
  uniqueness_score: number | null; is_unique: boolean;
  status: 'draft' | 'published'; published_url: string | null;
  published_at: string | null; created_at: string | null;
  image_url?: string | null;
  published_platforms?: string[];
}
export interface Web20CycleStatus {
  cycle_id: string; status: 'processing' | 'completed' | 'failed';
  articles_generated: number; total_platforms: number; articles: Web20Article[];
}
export interface Web20AuditResult {
  article_url: string; is_live: boolean; backlink_found: boolean;
  backlink_type: 'dofollow' | 'nofollow' | null; is_indexed: boolean;
  status: 'Optimal' | 'Needs Attention' | 'Critical'; notes: string;
}
export interface Web20TrackedAudit {
  id: string; article_url: string; platform: string | null;
  is_live: boolean | null; backlink_found: boolean | null; backlink_type: string | null;
  is_indexed: boolean | null; audit_status: string | null; notes: string | null;
  last_audited_at: string | null;
}
export interface Web20PrefillResponse {
  brand_name: string | null; website_url: string | null; primary_keyword: string | null;
  keywords: string[]; is_new_website: boolean; domain_age_months: number;
  available_platforms: Web20Platform[];
}

const BASE = '/implementation/off-page/web20';

export async function getWeb20Prefill(userId: string): Promise<Web20PrefillResponse> {
  return apiService.request<Web20PrefillResponse>(`${BASE}/prefill/${encodeURIComponent(userId)}`, { method: 'GET' });
}

export async function getAllWeb20Platforms(): Promise<Web20Platform[]> {
  const res = await apiService.request<{ platforms: Web20Platform[] }>(`${BASE}/platforms/all`, { method: 'GET' });
  return res.platforms;
}

export async function generateWeb20Articles(payload: {
  user_id: string; tone: string; selected_platforms?: string[]; primary_keyword?: string;
}): Promise<{ cycle_id: string; status: string; message: string; total_platforms: number }> {
  return apiService.request(`${BASE}/generate`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function getWeb20CycleStatus(cycleId: string): Promise<Web20CycleStatus> {
  return apiService.request<Web20CycleStatus>(`${BASE}/status/${encodeURIComponent(cycleId)}`, { method: 'GET' });
}

export async function getLatestWeb20Cycle(userId: string): Promise<Web20CycleStatus | null> {
  try {
    return await apiService.request<Web20CycleStatus>(`${BASE}/latest/${encodeURIComponent(userId)}`, { method: 'GET' });
  } catch { return null; }
}

export async function publishWeb20Article(
  articleId: string,
  publishedUrl: string,
  platform?: string
): Promise<Web20Article> {
  return apiService.request<Web20Article>(`${BASE}/articles/${encodeURIComponent(articleId)}/publish`, {
    method: 'PATCH',
    body: JSON.stringify({ published_url: publishedUrl, ...(platform ? { platform } : {}) }),
  });
}

export async function auditWeb20Submissions(payload: {
  article_urls: string[]; client_website_url: string; user_id?: string;
}): Promise<{ results: Web20AuditResult[] }> {
  return apiService.request(`${BASE}/audit`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function getWeb20AuditHistory(userId: string): Promise<{ user_id: string; total: number; results: Web20TrackedAudit[] }> {
  return apiService.request(`${BASE}/audits/${encodeURIComponent(userId)}`, { method: 'GET' });
}

export async function completeWeb20Article(
  articleId: string,
  payload: { platforms: string[] }
): Promise<Web20Article> {
  return apiService.request<Web20Article>(`${BASE}/articles/${encodeURIComponent(articleId)}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateWeb20ArticleContent(
  articleId: string,
  payload: { title?: string; content?: string }
): Promise<Web20Article> {
  return apiService.request<Web20Article>(`${BASE}/articles/${encodeURIComponent(articleId)}/content`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export const downloadS3ImageProxy = async (url: string) => {
  const token = localStorage.getItem('access_token');
  const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  const response = await fetch(`${apiUrl}/implementation/off-page/web20/download-image?url=${encodeURIComponent(url)}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) throw new Error('Failed to download image proxy');
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  const filename = url.split('/').pop()?.split('?')[0] || 'seo-image.png';
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 10000);
};

export async function getWeb20Library(userId: string): Promise<{ library: Web20Article[] }> {
  return apiService.request<{ library: Web20Article[] }>(`${BASE}/library/${encodeURIComponent(userId)}`, { method: 'GET' });
}
